/**
 * Normalización compartida de errores para el ErrorHandler global y los listeners
 * de `window` (`error` / `unhandledrejection`).
 *
 * Antes cada punto de captura tenía su propia lógica y ya habían divergido: uno
 * hasheaba el stack y el otro escribía la constante 'promise_rejection' a mano, uno
 * filtraba ruido y el otro no. Todo eso vive aquí ahora.
 */

const MAX_MESSAGE_LENGTH = 500;
const MAX_STACK_HEAD_LENGTH = 300;
const MAX_USER_AGENT_LENGTH = 300;

/** Tope de eventos por huella y por sesión, para que un error dentro de un ciclo
 *  de detección de cambios o un setInterval no inunde el buffer. */
const MAX_EVENTS_PER_FINGERPRINT = 5;

/** Errores originados en extensiones del browser (MetaMask y similares): no son
 *  código de la aplicación y solo generan ruido en BigQuery. */
const EXTENSION_STACK_PATTERN = /(?:chrome|moz|safari|ms-browser)-extension:\/\//i;

/** Patrones de errores esperados que no son bugs de la aplicación. */
const NOISE_PATTERNS: RegExp[] = [
  /play\(\)/i,                              // HTMLMediaElement: política de autoplay
  /user didn't interact/i,                  // autoplay bloqueado
  /interrupted by a call to pause/i,        // race condition audio
  /the operation was aborted/i,             // fetch cancelado al navegar
  /metamask/i,                              // extensión wallet cripto inyectada en la página
  /\bethereum\b/i,                          // idem: provider inyectado por la extensión
  /\bweb3\b/i,                              // idem
  // NOTA: "not allowed by user agent" (cámara denegada) NO se filtra
  // porque es útil para diagnosticar problemas de escaneo de QR.
];

export interface NormalizedError {
  /** Mensaje ya resuelto: nunca vacío, con fallback a String(error) / JSON. */
  message: string;
  /** `error.name`, o 'Error' si no hay. */
  type: string;
  /** Nombre del constructor: distingue un `throw {}` de un `throw new Error()`. */
  ctor?: string;
  /** Stack crudo, si existe. */
  stack?: string;
  /** Huella para agrupar en BigQuery. */
  stackHash: string;
  /** Primeros frames en crudo: sin esto el hash es una huella sin diccionario. */
  stackHead?: string;
  /** Código numérico de un RuntimeError de Angular (ej. 2100 para NG02100). */
  ngErrorCode?: number;
}

/**
 * Desenvuelve el error real. Angular/zone.js entregan al ErrorHandler un objeto
 * envoltorio cuyo error verdadero viaja en `rejection` o `ngOriginalError`; sin
 * desenvolverlo el payload sale vacío ("Unknown error").
 */
export function unwrapError(error: any): any {
  let current = error;

  for (let depth = 0; depth < 5; depth++) {
    const inner =
      current?.rejection ??
      current?.ngOriginalError ??
      (typeof ErrorEvent !== 'undefined' && current instanceof ErrorEvent ? current.error : undefined);

    if (inner == null || inner === current) break;
    current = inner;
  }

  return current;
}

export function normalizeError(error: any): NormalizedError {
  const stack = typeof error?.stack === 'string' ? error.stack : undefined;
  const message = resolveMessage(error);

  return {
    message,
    type: typeof error?.name === 'string' && error.name ? error.name : 'Error',
    ctor: error?.constructor?.name,
    stack,
    stackHash: hashStack(stack),
    stackHead: extractStackHead(stack),
    ngErrorCode: resolveNgErrorCode(error, message)
  };
}

/**
 * Resuelve el mensaje sin perder información cuando lo lanzado no es un `Error`.
 * `error?.message?.substring(...)` reventaba con un TypeError si `message` no era
 * string, y descartaba por completo los `throw 'texto'` y `throw {…}`.
 */
function resolveMessage(error: any): string {
  if (typeof error === 'string') return error;

  const message = error?.message;
  if (typeof message === 'string' && message.length > 0) return message;
  if (message != null && typeof message !== 'object') return String(message);

  if (error == null) return 'Unknown error';
  if (typeof error !== 'object') return String(error);

  try {
    const serialized = JSON.stringify(error);
    if (serialized && serialized !== '{}') return serialized;
  } catch {
    // referencias circulares: nos quedamos con el fallback
  }

  const asString = Object.prototype.toString.call(error);
  return asString === '[object Object]' ? 'Unknown error' : asString;
}

/**
 * En producción Angular deja solo el código pelado ("NG02100") porque el texto
 * descriptivo está detrás de `ngDevMode`. El código numérico permite filtrar en
 * BigQuery sin parsear strings.
 */
function resolveNgErrorCode(error: any, message: string): number | undefined {
  if (typeof error?.code === 'number') return error.code;

  const match = /\bNG0(\d{2,4})\b/.exec(message);
  return match ? Number(match[1]) : undefined;
}

/**
 * Huella del stack. La versión anterior solo conservaba líneas con 'at ', formato
 * exclusivo de Chromium: Firefox y Safari usan `fn@url:línea:columna`, así que el
 * filtro quedaba vacío y todos sus errores colapsaban en el hash de "" (sh_0).
 */
export function hashStack(stack?: string): string {
  if (!stack) return 'no_stack';

  const frames = extractFrames(stack).slice(0, 3);
  if (!frames.length) return 'no_stack';

  const fingerprint = frames.join('|');
  let hash = 0;
  for (let i = 0; i < fingerprint.length; i++) {
    hash = ((hash << 5) - hash) + fingerprint.charCodeAt(i);
    hash |= 0;
  }

  return `sh_${Math.abs(hash).toString(16)}`;
}

/** Primeros frames en crudo, para poder ubicar el hash en el código. */
function extractStackHead(stack?: string): string | undefined {
  if (!stack) return undefined;

  const head = extractFrames(stack).slice(0, 3).join(' | ');
  return head ? head.substring(0, MAX_STACK_HEAD_LENGTH) : undefined;
}

function extractFrames(stack: string): string[] {
  const lines = stack.split('\n').map(line => line.trim()).filter(Boolean);

  const frames = lines.filter(line =>
    /(^|\s)at\s/.test(line) ||        // Chromium: "at fn (url:1:2)"
    /@.*:\d+:\d+/.test(line)          // Firefox / Safari: "fn@url:1:2"
  );

  // Sin frames reconocibles nos quedamos con las líneas crudas menos la cabecera
  // ("Error: mensaje"), que ya viaja en error_message.
  return frames.length ? frames : lines.slice(1);
}

export function isNoiseError(normalized: NormalizedError): boolean {
  // Mensajes minificados de 1-2 caracteres ("Nd") no aportan nada.
  if (!normalized.message || normalized.message.length < 3) return true;
  if (normalized.stack && EXTENSION_STACK_PATTERN.test(normalized.stack)) return true;

  return NOISE_PATTERNS.some(pattern => pattern.test(normalized.message));
}

const reportedFingerprints = new Map<string, number>();

/** Corta el reporte tras MAX_EVENTS_PER_FINGERPRINT ocurrencias de la misma huella. */
export function shouldReport(normalized: NormalizedError): boolean {
  const fingerprint = `${normalized.type}:${normalized.stackHash}:${normalized.message.substring(0, 80)}`;
  const count = (reportedFingerprints.get(fingerprint) ?? 0) + 1;
  reportedFingerprints.set(fingerprint, count);

  return count <= MAX_EVENTS_PER_FINGERPRINT;
}

export function buildErrorPayload(normalized: NormalizedError): Record<string, any> {
  return {
    error_message: normalized.message.substring(0, MAX_MESSAGE_LENGTH),
    error_type: normalized.type,
    stack_hash: normalized.stackHash,
    ...(normalized.stackHead && { stack_head: normalized.stackHead }),
    ...(normalized.ctor && { error_ctor: normalized.ctor }),
    ...(normalized.ngErrorCode !== undefined && { ng_error_code: normalized.ngErrorCode }),
    ...buildClientContext()
  };
}

/**
 * `route` es `router.url`, que tras un fallo de lazy-load puede seguir apuntando a
 * la ruta anterior; `page_url` es la URL real. `user_agent` importa porque el
 * formato del stack (y por tanto el hash) depende del browser.
 */
export function buildClientContext(): Record<string, string> {
  try {
    return {
      page_url: window.location.href,
      user_agent: navigator.userAgent.substring(0, MAX_USER_AGENT_LENGTH)
    };
  } catch {
    return {};
  }
}
